import { useMemo, useState } from 'react';
import { Layers, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDecks } from '@/hooks/useDecks';
import {
  useMyMatches,
  computeDeckBreakdown,
  computeArchetypeBreakdown,
  type DeckBreakdownRow,
} from '@/hooks/useMatches';

const inkClass: Record<string, string> = {
  amber: 'bg-ink-amber',
  amethyst: 'bg-ink-amethyst',
  emerald: 'bg-ink-emerald',
  ruby: 'bg-ink-ruby',
  sapphire: 'bg-ink-sapphire',
  steel: 'bg-ink-steel',
};

export function DeckBreakdownCard() {
  const { user } = useAuth();
  const { data: matches } = useMyMatches();
  const { data: decks } = useDecks();
  const [mode, setMode] = useState<'deck' | 'archetype'>('deck');

  const deckRows = useMemo(
    () => computeDeckBreakdown(matches, user?.id, decks),
    [matches, user?.id, decks],
  );
  const archetypeRows = useMemo(
    () => computeArchetypeBreakdown(deckRows),
    [deckRows],
  );

  if (!user) return null;
  const rows = mode === 'deck' ? deckRows : archetypeRows;
  const hasLogged = deckRows.some(r => r.deckId);

  return (
    <div className="mt-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-foreground font-body flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 text-primary" /> Deck Breakdown
        </h3>
        <div className="flex items-center gap-1 rounded-lg bg-secondary p-0.5">
          {(['deck', 'archetype'] as const).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded-md px-2 py-1 text-[10px] font-medium font-body capitalize transition-colors ${
                mode === m ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {!rows.length ? (
        <p className="py-6 text-center text-[11px] text-muted-foreground font-body">
          Log a match with a deck selected to start tracking per-deck stats.
        </p>
      ) : (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto">
          {rows.map(r => (
            <BreakdownRow key={r.key} row={r} />
          ))}
        </ul>
      )}

      {!hasLogged && rows.length > 0 && (
        <p className="mt-3 flex items-start gap-1.5 rounded-md bg-secondary/60 px-2 py-1.5 text-[10px] text-muted-foreground font-body">
          <Sparkles className="h-3 w-3 mt-0.5 text-primary shrink-0" />
          New matches now auto-link to your most recently used deck.
        </p>
      )}
    </div>
  );
}

function BreakdownRow({ row }: { row: DeckBreakdownRow }) {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-2">
      <div className="flex -space-x-1">
        {row.ink_1 && <span className={`h-4 w-4 rounded-full ring-1 ring-card ${inkClass[row.ink_1] || 'bg-muted'}`} />}
        {row.ink_2 && row.ink_2 !== row.ink_1 && (
          <span className={`h-4 w-4 rounded-full ring-1 ring-card ${inkClass[row.ink_2] || 'bg-muted'}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-foreground font-body truncate">{row.label}</p>
        <p className="text-[9px] text-muted-foreground font-body">
          {row.wins}W · {row.losses}L{row.draws ? ` · ${row.draws}D` : ''}
        </p>
      </div>
      <div className="text-right">
        <p className="font-display text-sm font-bold text-primary tabular-nums">{row.winRate}%</p>
        <p className="text-[9px] text-muted-foreground font-body">{row.total} game{row.total === 1 ? '' : 's'}</p>
      </div>
    </li>
  );
}
